import React, { useState, useEffect, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { loginWithEmail, signUpWithEmail, loginWithGoogle } from '../lib/supabase';
import AgeConsent from '../components/AgeConsent';
import { markAgeConfirmed } from '../lib/ageConsent';
import { AnimatePresence } from 'motion/react';
import { ChevronRight, Mail, KeyRound, Eye, EyeOff, ArrowDown, Lock, Send, ShieldCheck, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import OnboardingModal from '../components/OnboardingModal';
import FloatingPhotos from './FloatingPhotos';
import NGLPopups from './NGLPopups';

interface HomeProps {
  user: User | null;
}

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function RevealBlock({
  children,
  delay = 0,
  className = '',
  from = 'bottom',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  from?: 'bottom' | 'left' | 'right' | 'top' | 'scale' | 'spin';
}) {
  const { ref, inView } = useInView();

  let transform = '';
  if (from === 'left')  transform = inView ? 'translateX(0) rotate(0deg) scale(1)' : 'translateX(-60px) rotate(-6deg) scale(0.85)';
  else if (from === 'right') transform = inView ? 'translateX(0) rotate(0deg) scale(1)' : 'translateX(60px) rotate(6deg) scale(0.85)';
  else if (from === 'top')   transform = inView ? 'translateY(0) scale(1)' : 'translateY(-40px) scale(0.9)';
  else if (from === 'scale') transform = inView ? 'scale(1) rotate(0deg)' : 'scale(0.5) rotate(-12deg)';
  else if (from === 'spin')  transform = inView ? 'scale(1) rotate(0deg)' : 'scale(0.3) rotate(20deg)';
  else transform = inView ? 'translateY(0) scale(1)' : 'translateY(48px) scale(0.92)';

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform,
        transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function Home({ user }: HomeProps) {
  const navigate = useNavigate();
  const { ref: heroRef, inView: heroInView } = useInView(0.05);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mode, setMode] = useState<'idle' | 'login' | 'signup'>('idle');
  const [ageOk, setAgeOk] = useState(false);
  const [ageInvalid, setAgeInvalid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [inquiryCount, setInquiryCount] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem('yhuu_onboarding_seen');
    if (!seen) {
      const t = setTimeout(() => setShowOnboarding(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const daysSinceEpoch = Math.floor(Date.now() / 86400000);
    const base = 94 + (daysSinceEpoch % 31);
    setInquiryCount(base);
    const tick = () => {
      setInquiryCount(c => c + 1);
      const next = 8000 + Math.random() * 12000;
      timer = setTimeout(tick, next);
    };
    let timer = setTimeout(tick, 8000 + Math.random() * 12000);
    return () => clearTimeout(timer);
  }, []);

  function handleCloseOnboarding() {
    setShowOnboarding(false);
    localStorage.setItem('yhuu_onboarding_seen', '1');
  }

  // "Get Started" on the final onboarding step — go straight into the
  // create-a-session flow instead of dropping the person back on the
  // landing page. Works for signed-out visitors too; CreateTest handles
  // getting them an account only once they've actually filled in a session.
  function handleGetStarted() {
    setShowOnboarding(false);
    localStorage.setItem('yhuu_onboarding_seen', '1');
    navigate('/create');
  }

  const handleSubmit = async () => {
    if (mode === 'signup' && !ageOk) { setAgeInvalid(true); return; }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await loginWithEmail(email, password);
      } else {
        markAgeConfirmed();
        await signUpWithEmail(email, password);
        setSuccess('Check your email to confirm your account.');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (mode === 'signup' && !ageOk) { setAgeInvalid(true); return; }
    markAgeConfirmed();
    setError('');
    setSuccess('');
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      // Browser will redirect to Google, then back to the app.
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
      setGoogleLoading(false);
    }
  };

  return (
    <>
      <style>{`
        /* hero keyframes removed — animations now driven by heroInView inline styles */
      `}</style>

      <div className="flex flex-col items-center" style={{ background: 'var(--color-brand-bg)' }}>
        {showOnboarding && <OnboardingModal onClose={handleCloseOnboarding} onGetStarted={handleGetStarted} />}

        {/* ── HERO ── */}
        <section ref={heroRef} className="w-full max-w-4xl pt-20 pb-16 px-4 text-center" style={{ position: 'relative', minHeight: 620 }}>
          <FloatingPhotos />
          <NGLPopups />

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-2 leading-[1.15]"
            style={{
              color: 'var(--color-brand-text)',
              opacity: heroInView ? 1 : 0,
              transform: heroInView ? 'translateY(0) scale(1) rotate(0deg)' : 'translateY(-50px) scale(1.1) rotate(-2deg)',
              transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1) 0.35s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.35s',
            }}>
            Want Honest Answers?
          </h1>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6 leading-[1.15]"
            style={{
              color: 'var(--color-brand-text)',
              opacity: heroInView ? 1 : 0,
              transform: heroInView ? 'translateX(0) rotate(0deg) scale(1)' : 'translateX(80px) rotate(8deg) scale(0.8)',
              transition: 'opacity 0.75s cubic-bezier(0.22,1,0.36,1) 0.55s, transform 0.75s cubic-bezier(0.22,1,0.36,1) 0.55s',
            }}>
            <span className="font-serif italic text-brand-accent">Ask Anonymously Now.</span>
          </h1>

          <p className="text-base sm:text-lg mb-8 max-w-xl mx-auto leading-relaxed font-light text-zinc-500" style={{
            opacity: heroInView ? 1 : 0,
            transform: heroInView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
            filter: heroInView ? 'blur(0px)' : 'blur(4px)',
            transition: 'opacity 0.8s cubic-bezier(0.22,1,0.36,1) 0.7s, transform 0.8s cubic-bezier(0.22,1,0.36,1) 0.7s, filter 0.8s cubic-bezier(0.22,1,0.36,1) 0.7s',
          }}>
            No link to share, no waiting for them to click it. Just tell us who you want to ask, and send your anonymous question straight to them — right now.
          </p>

          {/* live pill */}
          <div className="flex items-center justify-center gap-2 mb-6 mt-2" style={{
            opacity: heroInView ? 1 : 0,
            transform: heroInView ? 'scale(1) translateY(0)' : 'scale(0.4) translateY(10px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.75s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.75s',
          }}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-sm font-semibold text-emerald-400">{inquiryCount}</span>
            <span className="text-sm text-zinc-500">sessions sent today</span>
          </div>

          <div className="flex flex-col items-center gap-6">
            {user ? (
              <div style={{
                opacity: heroInView ? 1 : 0,
                transform: heroInView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.8)',
                transition: 'opacity 0.65s cubic-bezier(0.22,1,0.36,1) 0.85s, transform 0.65s cubic-bezier(0.22,1,0.36,1) 0.85s',
              }}>
                <Link
                  to="/dashboard"
                  className="w-full max-w-xs px-8 py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-3 group border border-brand-border"
                  style={{ background: 'var(--color-brand-card)', color: 'var(--color-brand-text)' }}
                >
                  Open Yhuu
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                  <div className="flex-1" style={{
                    opacity: heroInView ? 1 : 0,
                    transform: heroInView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.8)',
                    transition: 'opacity 0.65s cubic-bezier(0.22,1,0.36,1) 0.85s, transform 0.65s cubic-bezier(0.22,1,0.36,1) 0.85s',
                  }}>
                    <button
                      onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                      data-track="home_sign_in"
                      className="w-full px-6 py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 group border border-brand-border cursor-pointer"
                      style={{ background: 'var(--color-brand-card)', color: 'var(--color-brand-text)' }}
                    >
                      Sign In
                      <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                  <div className="flex-1" style={{
                    opacity: heroInView ? 1 : 0,
                    transform: heroInView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.8)',
                    transition: 'opacity 0.65s cubic-bezier(0.22,1,0.36,1) 1.0s, transform 0.65s cubic-bezier(0.22,1,0.36,1) 1.0s',
                  }}>
                    <button
                      onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}
                      data-track="home_start_asking"
                      className="w-full px-6 py-4 bg-brand-accent text-white rounded-xl font-bold transition-all hover:opacity-90 flex items-center justify-center gap-2 group shadow-2xl shadow-rose-900/40 cursor-pointer"
                    >
                      Start asking — it's free
                      <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>

                <AnimatePresence>
                  {mode !== 'idle' && (
                    <div
                      className="w-full max-w-sm rounded-2xl p-6 mt-2 text-left border border-brand-border"
                      style={{ background: 'var(--color-brand-card)' }}
                    >
                      <h2 className="font-semibold text-lg mb-6" style={{ color: 'var(--color-brand-text)' }}>
                        {mode === 'login' ? 'Sign In' : 'Create Account'}
                      </h2>
                      <div className="flex flex-col gap-4">
                        {mode !== 'idle' && (
                          <AgeConsent mode={mode} checked={ageOk} onChange={v => { setAgeOk(v); if (v) setAgeInvalid(false); }} invalid={ageInvalid} />
                        )}
                        <button
                          type="button"
                          onClick={handleGoogleLogin}
                          disabled={googleLoading}
                          className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 border border-brand-border transition-all hover:opacity-90 disabled:opacity-50 cursor-pointer"
                          style={{ background: 'var(--color-brand-bg)', color: 'var(--color-brand-text)' }}
                        >
                          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.8 2.73v2.27h2.91c1.7-1.57 2.69-3.88 2.69-6.64z" />
                            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.17l-2.91-2.27c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.34C2.44 15.98 5.48 18 9 18z" />
                            <path fill="#FBBC05" d="M3.95 10.71c-.18-.54-.28-1.11-.28-1.71s.1-1.17.28-1.71V4.95H.96A8.996 8.996 0 000 9c0 1.45.35 2.83.96 4.05l2.99-2.34z" />
                            <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.95l2.99 2.34C4.66 5.16 6.65 3.58 9 3.58z" />
                          </svg>
                          {googleLoading ? 'Redirecting...' : 'Continue with Google'}
                        </button>

                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-px" style={{ background: 'var(--color-brand-border)' }} />
                          <span className="text-xs text-zinc-500">or</span>
                          <div className="flex-1 h-px" style={{ background: 'var(--color-brand-border)' }} />
                        </div>

                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                          <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:border-brand-accent border border-brand-border"
                            style={{ background: 'var(--color-brand-bg)', color: 'var(--color-brand-text)' }}
                          />
                        </div>
                        <div className="relative">
                          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                            className="w-full pl-10 pr-10 py-3 rounded-xl text-sm focus:outline-none focus:border-brand-accent border border-brand-border"
                            style={{ background: 'var(--color-brand-bg)', color: 'var(--color-brand-text)' }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(prev => !prev)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {error && <p className="text-rose-400 text-sm">{error}</p>}
                        {success && <p className="text-emerald-400 text-sm">{success}</p>}
                        <button
                          onClick={handleSubmit}
                          disabled={loading}
                          className="w-full py-3 bg-brand-accent text-white rounded-xl font-bold hover:opacity-90 transition-all disabled:opacity-50 cursor-pointer"
                        >
                          {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                        </button>
                        <button
                          onClick={() => setMode('idle')}
                          className="text-zinc-500 text-xs text-center hover:text-zinc-300 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </AnimatePresence>
              </>
            )}
          </div>

          <div className="mt-6" style={{
            opacity: heroInView ? 1 : 0,
            transform: heroInView ? 'rotate(0deg) scale(1)' : 'rotate(-20deg) scale(0.5)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 1.15s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 1.15s',
          }}>
            <button
              onClick={() => setShowOnboarding(true)}
              data-track="home_how_it_works"
              className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-brand-accent transition-colors mx-auto"
            >
              <Sparkles className="w-3.5 h-3.5" />
              How does Yhuu work?
            </button>
          </div>
        </section>

        {/* ── VIBE CHECK PREVIEW ── */}
        <RevealBlock className="w-full max-w-sm px-4 pb-10">
          <div
            className="rounded-2xl border border-brand-border p-5"
            style={{ background: 'var(--color-brand-card)' }}
          >
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 mb-4">Vibe Check — expected vs. actual</p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-zinc-600 w-16 shrink-0">Expected</span>
                <span className="flex-1 px-3 py-2 rounded-lg text-sm text-zinc-500 line-through decoration-zinc-700" style={{ background: 'var(--color-brand-bg)' }}>
                  yes, obviously
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-zinc-600 w-16 shrink-0">They said</span>
                <span className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-brand-accent border border-brand-accent/20" style={{ background: 'rgba(232,54,93,0.06)' }}>
                  maybe... idk 👀
                </span>
              </div>
            </div>
          </div>
        </RevealBlock>

        {/* ── FEATURES GRID ── */}
        <section className="grid grid-cols-1 gap-4 w-full max-w-6xl px-4 pb-16">
          {[
            {
              icon: <Lock className="w-5 h-5 text-brand-accent" />,
              title: 'Encrypted Records',
              desc: 'Your session stays fully private, locked away where only you can see it.',
              delay: 0,
              from: 'left' as const,
            },
            {
              icon: <Send className="w-5 h-5 text-zinc-400" />,
              title: 'Silent Hand-off',
              desc: 'Automated delivery across WhatsApp, Snapchat, or Telegram. We handle the outreach while you remain invisible.',
              delay: 0.1,
              from: 'bottom' as const,
            },
            {
              icon: <ShieldCheck className="w-5 h-5 text-emerald-500/70" />,
              title: 'Vibe Check',
              desc: 'See how their answers stack up against what you expected. Spot the mixed signals instantly.',
              delay: 0.2,
              from: 'right' as const,
            },
          ].map(f => (
            <RevealBlock key={f.title} delay={f.delay} from={f.from}>
              <div
                className="p-8 rounded-2xl border border-brand-border transition-colors"
                style={{ background: 'var(--color-brand-card)' }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-6 border border-brand-border"
                  style={{ background: 'var(--color-brand-bg)' }}
                >
                  {f.icon}
                </div>
                <h3 className="text-lg font-medium mb-3" style={{ color: 'var(--color-brand-text)' }}>{f.title}</h3>
                <p className="text-zinc-500 leading-relaxed font-light text-sm">{f.desc}</p>
              </div>
            </RevealBlock>
          ))}
        </section>

        {/* ── FOOTER ── */}
        <RevealBlock className="w-full">
          <section className="w-full border-t border-brand-border pt-12 pb-10 px-4 text-center space-y-5">
            <div className="flex items-center justify-center flex-wrap gap-6">
              <Link to="/about" className="text-sm text-zinc-500 hover:text-brand-accent transition-colors font-medium underline underline-offset-2">
                About
              </Link>
              <Link to="/safety" className="text-sm text-zinc-500 hover:text-brand-accent transition-colors font-medium underline underline-offset-2">
                Safety
              </Link>
              <a href="mailto:new@yhuu.life" className="text-sm text-zinc-500 hover:text-brand-accent transition-colors font-medium underline underline-offset-2">
                Contact us
              </a>
            </div>
            <div className="flex items-center justify-center flex-wrap gap-5">
              <Link to="/terms" className="text-xs text-zinc-600 hover:text-brand-accent transition-colors underline underline-offset-2">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-xs text-zinc-600 hover:text-brand-accent transition-colors underline underline-offset-2">
                Privacy Policy
              </Link>
            </div>
            <p className="text-xs text-zinc-600 uppercase tracking-[0.2em] font-medium leading-loose">
              © {new Date().getFullYear()} Yhuu <br />
              <span className="opacity-50">Established for Relationship Integrity</span>
            </p>
          </section>
        </RevealBlock>
      </div>
    </>
  );
}
